import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { CheckCircle2, Star, Zap, Crown, Building2, ArrowRight, Gift } from 'lucide-react';
import { buildApiUrl } from '@/config/api';

const PricingPage = ({ isPublic = false }) => {
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creditCouponCode, setCreditCouponCode] = useState('');
  const [creditRedemptionResult, setCreditRedemptionResult] = useState(null);
  const [isRedeemingCredits, setIsRedeemingCredits] = useState(false);
  const navigate = useNavigate();
  const user = null; // Temporarily set to null since useAuth doesn't exist

  // Define plans with best practice pricing
  const plans = [
    {
      id: 1,
      name: 'Starter',
      description: 'Perfect for small teams and startups',
      price: 49,
      billing_cycle: 'monthly',
      interview_credits: 10,
      features: [
        '10 interviews per month',
        'Basic AI assessment',
        'Email support',
        'Standard templates',
        'Basic analytics'
      ],
      popular: false,
      icon: Zap
    },
    {
      id: 2,
      name: 'Professional',
      description: 'Ideal for growing companies',
      price: 149,
      billing_cycle: 'monthly',
      interview_credits: 50,
      features: [
        '50 interviews per month',
        'Advanced AI assessment',
        'Priority support',
        'Custom templates',
        'Advanced analytics',
        'Team collaboration',
        'API access'
      ],
      popular: true,
      icon: Star
    },
    {
      id: 3,
      name: 'Enterprise',
      description: 'For large organizations',
      price: 399,
      billing_cycle: 'monthly',
      interview_credits: 200,
      features: [
        '200 interviews per month',
        'Premium AI assessment',
        '24/7 dedicated support',
        'White-label solution',
        'Advanced analytics & reporting',
        'Custom integrations',
        'SSO authentication',
        'Dedicated account manager'
      ],
      popular: false,
      icon: Crown
    },
    {
      id: 4,
      name: 'Pay Per Interview',
      description: 'Flexible pricing for occasional use',
      price: 50,
      billing_cycle: 'per_interview',
      interview_credits: 1,
      features: [
        '$50 per interview',
        'No monthly commitment',
        'All premium features',
        'Email support',
        'Full AI assessment'
      ],
      popular: false,
      icon: Building2
    }
  ];

  const applyCoupon = () => {
    if (!couponCode.trim()) return;

    // Handle the special "hirehubble" coupon
    if (couponCode.toLowerCase() === 'hirehubble') {
      setAppliedCoupon({
        code: 'hirehubble',
        discount_type: 'percentage',
        discount_value: 20,
        description: '20% off your first month - Welcome to AI Recruiter!'
      });
    } else {
      alert('Invalid coupon code. Try "hirehubble" for 20% off!');
    }
  };

  const redeemCreditsCode = async () => {
    if (!creditCouponCode.trim()) {
      alert('Please enter a coupon code');
      return;
    }

    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      // Redirect to login with return URL
      const returnUrl = encodeURIComponent('/pricing');
      navigate(`/login?returnUrl=${returnUrl}&message=Please log in to redeem coupon codes`);
      return;
    }

    setIsRedeemingCredits(true);
    setCreditRedemptionResult(null);

    try {
      const response = await fetch(buildApiUrl('coupons/redeem'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          code: creditCouponCode.trim()
        })
      });

      const result = await response.json();

      if (result.success) {
        setCreditRedemptionResult({
          success: true,
          message: result.data.message,
          credits_added: result.data.credits_added
        });
        
        // Clear the input
        setCreditCouponCode('');
        
        // If credits were added, show success and offer redirect
        if (result.data.credits_added > 0) {
          setTimeout(() => {
            const confirmRedirect = window.confirm(
              `Great! You've received ${result.data.credits_added} interview credits. Would you like to schedule an interview now?`
            );
            if (confirmRedirect) {
              navigate('/recruiter'); // Navigate to interview scheduling
            }
          }, 2000);
        }
      } else {
        setCreditRedemptionResult({
          success: false,
          message: result.data?.message || 'Failed to redeem coupon'
        });
      }
    } catch (error) {
      console.error('Error redeeming coupon:', error);
      setCreditRedemptionResult({
        success: false,
        message: 'Error connecting to server. Please try again.'
      });
    } finally {
      setIsRedeemingCredits(false);
    }
  };

  const calculateDiscountedPrice = (originalPrice, coupon) => {
    if (!coupon) return originalPrice;

    switch (coupon.discount_type) {
      case 'percent':
        return originalPrice * (1 - coupon.discount_value / 100);
      case 'flat':
        return Math.max(0, originalPrice - coupon.discount_value);
      case 'free_interviews':
        return originalPrice; // Handled differently
      default:
        return originalPrice;
    }
  };

  const getPlanIcon = (planName) => {
    switch (planName.toLowerCase()) {
      case 'free':
        return <Star className="w-6 h-6 text-green-500" />;
      case 'basic':
        return <Zap className="w-6 h-6 text-blue-500" />;
      case 'pro':
        return <Crown className="w-6 h-6 text-purple-500" />;
      case 'enterprise':
        return <Building2 className="w-6 h-6 text-orange-500" />;
      default:
        return <CheckCircle2 className="w-6 h-6 text-gray-500" />;
    }
  };

  const getPlanColor = (planName) => {
    switch (planName.toLowerCase()) {
      case 'free':
        return 'border-green-200 bg-green-50';
      case 'basic':
        return 'border-blue-200 bg-blue-50';
      case 'pro':
        return 'border-purple-200 bg-purple-50 ring-2 ring-purple-500';
      case 'enterprise':
        return 'border-orange-200 bg-orange-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const handleSubscribe = (plan) => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      // Redirect to login with return URL
      const returnUrl = encodeURIComponent(`/checkout?planId=${plan.id}&planName=${plan.name}&price=${plan.price}`);
      navigate(`/login?returnUrl=${returnUrl}`);
      return;
    }

    // Navigate to checkout with plan details
    const discountedPrice = calculateDiscountedPrice(plan.price, appliedCoupon);
    const queryParams = new URLSearchParams({
      planId: plan.id,
      planName: plan.name,
      price: discountedPrice,
      originalPrice: plan.price,
      billingCycle: plan.billing_cycle,
      interviewCredits: plan.interview_credits,
    });

    if (appliedCoupon) {
      queryParams.append('couponCode', appliedCoupon.code);
      queryParams.append('discountType', appliedCoupon.discount_type);
      queryParams.append('discountValue', appliedCoupon.discount_value);
    }

    navigate(`/checkout?${queryParams.toString()}`);
  };

  const handlePayPerUse = () => {
    navigate('/pay-per-interview');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="pt-16 pb-12 text-center">
        <h1 className="text-5xl font-bold text-gray-900 mb-4">
          Choose Your Perfect Plan
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Transform your hiring process with AI-powered interviews. Find the perfect plan
          for your team size and needs.
        </p>
      </div>

      {/* Free Credits Coupon Section */}
      <div className="max-w-md mx-auto mb-8 px-4">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg shadow-sm p-6">
          <div className="text-center pb-3 border-b border-green-200 mb-4">
            <h3 className="text-lg font-semibold text-green-700 flex items-center justify-center gap-2">
              <Gift className="w-5 h-5" />
              Get Free Interview Credits!
            </h3>
            <p className="text-sm text-gray-600 mt-1">Redeem coupon codes for free interview credits</p>
            <p className="text-xs text-green-600 mt-1 font-medium">Try: WELCOME50, TESTBONUS, or FREECREDITS10</p>
          </div>
          <div>
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Enter coupon code for credits"
                  value={creditCouponCode}
                  onChange={(e) => setCreditCouponCode(e.target.value.toUpperCase())}
                  className="w-full border border-green-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-900 bg-white"
                  disabled={isRedeemingCredits}
                />
              </div>
              <button
                onClick={redeemCreditsCode}
                disabled={!creditCouponCode.trim() || isRedeemingCredits}
                className="px-4 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isRedeemingCredits ? 'Redeeming...' : 'Redeem'}
              </button>
            </div>
            {creditRedemptionResult && (
              <div className={`mt-3 p-3 rounded-lg border ${
                creditRedemptionResult.success
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <p className={`text-sm font-medium ${
                  creditRedemptionResult.success ? 'text-green-700' : 'text-red-700'
                }`}>
                  {creditRedemptionResult.success ? '🎉' : '❌'} {creditRedemptionResult.message}
                </p>
                {creditRedemptionResult.success && creditRedemptionResult.credits_added > 0 && (
                  <p className="text-xs text-green-600 mt-1">
                    You can now schedule interviews with your credits!
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Discount Coupon Section */}
      <div className="max-w-md mx-auto mb-12 px-4">
        <div className="bg-white/90 backdrop-blur-sm border border-yellow-200 rounded-lg shadow-sm p-6">
          <div className="text-center pb-3 border-b border-gray-200 mb-4">
            <h3 className="text-lg font-semibold text-yellow-700 flex items-center justify-center gap-2">
              <Star className="w-5 h-5" />
              Subscription Discount Available!
            </h3>
            <p className="text-sm text-gray-600 mt-1">Use code "hirehubble" for 20% off</p>
          </div>
          <div>
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder='Try "hirehubble" for 20% discount'
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  className="w-full border border-yellow-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-gray-900 bg-white"
                />
              </div>
              <button
                onClick={applyCoupon}
                disabled={!couponCode.trim()}
                className="px-4 py-2 text-sm font-medium border border-yellow-300 text-yellow-700 bg-white hover:bg-yellow-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Apply
              </button>
            </div>
            {appliedCoupon && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 font-medium">
                  ✅ Coupon Applied: {appliedCoupon.code}
                </p>
                <p className="text-xs text-green-600">
                  {appliedCoupon.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {plans.map((plan, index) => {
            const originalPrice = parseFloat(plan.price);
            const discountedPrice = calculateDiscountedPrice(originalPrice, appliedCoupon);
            const isPopular = plan.popular;
            const IconComponent = plan.icon;

            return (
              <div key={plan.id} className={`relative ${getPlanColor(plan.name)} border-2 transition-all duration-300 hover:shadow-xl hover:scale-105 bg-white rounded-lg shadow-sm`}>
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center pb-4 p-6 border-b border-gray-200">
                  <div className="flex justify-center mb-4">
                    <IconComponent className="w-12 h-12 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 min-h-[3rem] mt-2">
                    {plan.description}
                  </p>
                  
                  <div className="mt-6">
                    {plan.billing_cycle === 'per_interview' ? (
                      <div>
                        <div className="text-4xl font-bold text-gray-900">
                          ${originalPrice}
                        </div>
                        <p className="text-gray-600 text-sm">per interview</p>
                      </div>
                    ) : (
                      <div>
                        {appliedCoupon && discountedPrice !== originalPrice ? (
                          <div>
                            <div className="text-2xl font-bold text-gray-400 line-through">
                              ${originalPrice}/month
                            </div>
                            <div className="text-4xl font-bold text-green-600">
                              ${discountedPrice.toFixed(2)}
                              <span className="text-lg font-medium text-gray-600">/month</span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-4xl font-bold text-gray-900">
                            ${originalPrice}
                            <span className="text-lg font-medium text-gray-600">/month</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {/* Features List */}
                  <div className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Special Coupon Benefits */}
                  {appliedCoupon && appliedCoupon.discount_type === 'free_interviews' && (
                    <div className="p-3 bg-green-100 border border-green-300 rounded-lg">
                      <p className="text-sm text-green-700 font-medium">
                        🎉 Bonus: {appliedCoupon.discount_value} Free Interviews
                      </p>
                    </div>
                  )}

                  {/* Subscribe Button */}
                  <button
                    onClick={() => handleSubscribe(plan)}
                    className={`w-full px-4 py-3 font-medium rounded-lg transition-colors flex items-center justify-center ${
                      isPopular
                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                        : plan.name === 'Free'
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {plan.name === 'Free' ? 'Get Started Free' : 'Subscribe Now'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pay Per Use Section */}
        <div className="text-center mb-16">
          <div className="max-w-md mx-auto bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-0 rounded-lg shadow-sm p-6">
            <div className="text-center pb-4 border-b border-indigo-400 mb-4">
              <h3 className="text-2xl font-semibold">Need Flexibility?</h3>
              <p className="text-indigo-100 mt-2">
                Pay only for the interviews you conduct
              </p>
            </div>
            <div>
              <div className="text-3xl font-bold mb-4">$5 per interview</div>
              <button
                onClick={handlePayPerUse}
                className="w-full bg-white text-indigo-600 hover:bg-gray-100 px-4 py-3 font-medium rounded-lg transition-colors flex items-center justify-center"
              >
                Pay Per Use
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm p-6">
              <div className="pb-4 border-b border-gray-200 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Can I change plans anytime?</h3>
              </div>
              <div>
                <p className="text-gray-600">
                  Yes! You can upgrade or downgrade your plan at any time. Changes will be 
                  prorated and reflected in your next billing cycle.
                </p>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm p-6">
              <div className="pb-4 border-b border-gray-200 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Do you offer refunds?</h3>
              </div>
              <div>
                <p className="text-gray-600">
                  We offer a 30-day money-back guarantee for all paid plans. 
                  Contact our support team for assistance.
                </p>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm p-6">
              <div className="pb-4 border-b border-gray-200 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">What payment methods do you accept?</h3>
              </div>
              <div>
                <p className="text-gray-600">
                  We accept all major credit cards, PayPal, and bank transfers 
                  for Enterprise plans.
                </p>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm p-6">
              <div className="pb-4 border-b border-gray-200 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Is there a setup fee?</h3>
              </div>
              <div>
                <p className="text-gray-600">
                  No setup fees! All plans include free onboarding and setup assistance 
                  to get you started quickly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
